import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Boxes, ListChecks, ShoppingBag, Truck, UserPlus, Wallet } from 'lucide-react';

import { SiteAnalytics } from '@/components/dashboard/site-analytics';
import { EmptyState } from '@/components/settings/settings-kit';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  getRecentOrders,
  getTodaySnapshot,
  getTopProducts,
  type TodaySnapshot,
} from '@/lib/dashboard';

import { useAuth } from '../lib/auth.tsx';

/**
 * The overview.
 *
 * Six numbers about right now, two short lists -- what sold today and what
 * came in most recently -- and the website panel beneath them. Everything here is a link: a dashboard whose
 * numbers cannot be opened is a poster, and the reason to look at one is
 * always the thing behind it.
 */
export const Route = createFileRoute('/')({
  component: OverviewPage,
});

const usd = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

function OverviewPage() {
  const { activeAccountId } = useAuth();
  const accountId = activeAccountId ?? '';

  const snapshot = useQuery({
    queryKey: ['dashboard-today', accountId],
    queryFn: () => getTodaySnapshot(accountId),
    enabled: Boolean(accountId),
    // The shop moves while this is open; a minute is fresh enough for a
    // number nobody acts on without opening the screen behind it.
    refetchInterval: 60_000,
  });

  const top = useQuery({
    queryKey: ['dashboard-top', accountId],
    queryFn: () => getTopProducts(accountId),
    enabled: Boolean(accountId),
  });

  const recent = useQuery({
    queryKey: ['dashboard-recent', accountId],
    queryFn: () => getRecentOrders(accountId),
    enabled: Boolean(accountId),
  });

  const today: TodaySnapshot | undefined = snapshot.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Today</h1>
        <p className="text-muted-foreground text-sm">
          {new Intl.DateTimeFormat('en-US', { dateStyle: 'full' }).format(new Date())}
        </p>
      </div>

      {snapshot.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2, 3, 4, 5].map((key) => (
            <Skeleton key={key} className="h-24 w-full" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Metric
            to="/orders"
            icon={ShoppingBag}
            label="Orders today"
            value={String(today?.orders ?? 0)}
            hint={today?.orders ? `${usd(today.averageOrderCents)} average` : 'Nothing yet'}
          />
          <Metric
            to="/orders"
            icon={Wallet}
            label="Revenue today"
            value={usd(today?.revenueCents ?? 0)}
            hint="Cancelled orders excluded"
          />
          <Metric
            to="/queue"
            icon={ListChecks}
            label="In the shop"
            value={String(today?.queueDepth ?? 0)}
            hint="Waiting, shopping and at the register"
          />
          <Metric
            to="/deliveries"
            icon={Truck}
            label="Deliveries out"
            value={String(today?.deliveriesInFlight ?? 0)}
            hint="Assigned, on the way or arrived"
          />
          <Metric
            to="/inventory"
            icon={Boxes}
            label="Low stock"
            value={String(today?.lowStock ?? 0)}
            hint="At or under their threshold"
            warn={(today?.lowStock ?? 0) > 0}
          />
          <Metric
            to="/customers"
            icon={UserPlus}
            label="New customers"
            value={String(today?.newCustomers ?? 0)}
            hint="Registered today"
          />
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-lg border">
          <header className="border-b px-4 py-3">
            <h2 className="text-sm font-semibold">Moving today</h2>
          </header>

          {top.isLoading ? (
            <Skeleton className="m-4 h-32" />
          ) : top.data?.length ? (
            <ul className="divide-y">
              {top.data.map((product) => (
                <li
                  key={product.name}
                  className="flex items-baseline justify-between gap-3 px-4 py-2.5"
                >
                  <span className="min-w-0 truncate text-sm">{product.name}</span>
                  <span className="text-muted-foreground shrink-0 text-sm tabular-nums">
                    {product.quantity} · {usd(product.revenueCents)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState>Nothing sold today yet.</EmptyState>
          )}
        </section>

        <section className="rounded-lg border">
          <header className="border-b px-4 py-3">
            <h2 className="text-sm font-semibold">Latest orders</h2>
          </header>

          {recent.isLoading ? (
            <Skeleton className="m-4 h-32" />
          ) : recent.data?.length ? (
            <ul className="divide-y">
              {recent.data.map((order) => (
                <li key={order.id}>
                  <Link
                    to="/orders/$orderId"
                    params={{ orderId: order.id }}
                    className="hover:bg-muted/50 flex items-baseline justify-between gap-3 px-4 py-2.5"
                  >
                    <span className="min-w-0 truncate text-sm">
                      #{order.orderNumber} · {order.customerName}
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <Badge variant="secondary">{order.status}</Badge>
                      <span className="text-sm tabular-nums">{usd(order.totalCents)}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState>No orders yet.</EmptyState>
          )}
        </section>
      </div>

      {accountId && <SiteAnalytics accountId={accountId} />}
    </div>
  );
}

function Metric({
  to,
  icon: Icon,
  label,
  value,
  hint,
  warn,
}: {
  to: string;
  icon: typeof ShoppingBag;
  label: string;
  value: string;
  hint: string;
  warn?: boolean;
}) {
  return (
    <Link to={to} className="hover:bg-muted/40 block rounded-lg border p-4 transition-colors">
      <p className="text-muted-foreground flex items-center gap-2 text-xs">
        <Icon className="size-3.5" />
        {label}
      </p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums ${warn ? 'text-destructive' : ''}`}>
        {value}
      </p>
      <p className="text-muted-foreground mt-0.5 text-xs">{hint}</p>
    </Link>
  );
}
